import { useCallback, useEffect, useRef, useState } from 'react'
import { FilterBar } from './components/FilterBar'
import { DataQualityPage } from './components/DataQualityPage'
import { MetricsCatalogPage } from './components/MetricsCatalogPage'
import { SettingsPage } from './components/SettingsPage'
import { DatePicker } from './components/DatePicker'
import { LoginPage } from './components/LoginPage'
import { OrderDetailModal } from './components/OrderDetailModal'
import { DashboardPage } from './components/DashboardPage'
import { ExplorerPage } from './components/ExplorerPage'
import { ProcessDebugPanel } from './components/ProcessDebugPanel'
import { ConfirmProcessChangeModal } from './components/ConfirmProcessChangeModal'
import { useAuth } from './hooks/useAuth'
import { useCompanies } from './hooks/useCompanies'
import { useOrders } from './hooks/useOrders'
import { useRoi } from './hooks/useRoi'
import { useSegmentProcess } from './hooks/useSegmentProcess'
import { useWebSocket } from './hooks/useWebSocket'
import { readUrlFilters, writeUrlFilters } from './utils/urlFilters'
import { describeParamChanges, describeParams, joinChanges, ProcessParams } from './utils/process-params'
import { Company, LiveAttributionStats, LiveAttributionWindow, LIVE_ATTRIBUTION_WINDOW_LABELS, LIVE_ATTRIBUTION_WINDOW_ORDER, Order, ProcessProgress, SegmentProcessResult, StoryConversion } from './types'

type AppPage = 'dashboard' | 'data-quality' | 'explorer' | 'metrics' | 'settings'
type ProductMode = 'stories' | 'live'
type UserMode = 'analyst' | 'scientist'

// Filtros da URL lidos UMA vez, na carga da página (link compartilhado).
// Depois disso a URL é derivada do estado (writeUrlFilters), nunca relida.
const initialUrlFilters = readUrlFilters()

function App() {
  // Handle /logout route - clears Firebase auth state without needing SDK
  if (window.location.pathname === '/logout' || window.location.search.includes('logout')) {
    // Clear all Firebase auth storage
    const keysToRemove = Object.keys(localStorage).filter(k => k.startsWith('firebase:'))
    keysToRemove.forEach(k => localStorage.removeItem(k))
    sessionStorage.clear()
    // Clear IndexedDB (Firebase Auth stores tokens here)
    indexedDB.databases?.().then(dbs => dbs.forEach(db => { if (db.name) indexedDB.deleteDatabase(db.name) }))
    window.location.href = '/'
    return <div style={{ padding: 40, textAlign: 'center' }}>Saindo...</div>
  }

  const {
    user,
    loading: authLoading,
    error: authError,
    mfa,
    signInWithGoogle,
    verifyMfaCode,
    resendMfaCode,
    cancelMfa,
    signOut
  } = useAuth()

  if (authLoading) {
    return (
      <div className="login-container">
        <div className="spinner" style={{ width: 24, height: 24 }} />
      </div>
    )
  }

  if (!user) {
    return (
      <LoginPage
        onSignIn={signInWithGoogle}
        error={authError}
        mfa={mfa}
        onVerifyMfaCode={verifyMfaCode}
        onResendMfaCode={resendMfaCode}
        onCancelMfa={cancelMfa}
      />
    )
  }

  return <AppContent user={user} onSignOut={signOut} />
}

function AppContent({
  user,
  onSignOut
}: {
  user: { email: string | null }
  onSignOut: () => void
}) {
  const [activePage, setActivePage] = useState<AppPage>('dashboard')
  const [productMode, setProductMode] = useState<ProductMode>(initialUrlFilters.productMode ?? 'stories')
  const [userMode] = useState<UserMode>('analyst')
  const {
    progress,
    isConnected,
    sessionId: processSessionId,
    startSession,
    clearProgress,
    processEvents,
    processStatus,
    processStartedAt,
    cancelProcess,
  } = useWebSocket()
  // Empresa e grupo são rastreados no backend (etapas, cancelamento e
  // recuperação ao reconectar).
  //
  // Cancelar ANTES de o backend devolver a sessão (clique logo depois de
  // "Processar") não tem o que cancelar ainda: fica anotado e é feito assim
  // que a sessão chega.
  const awaitingSessionRef = useRef(false)
  const cancelOnStartRef = useRef(false)
  const handleSessionStart = useCallback(
    (id: string) => {
      awaitingSessionRef.current = false
      startSession(id, true)
      if (cancelOnStartRef.current) {
        cancelOnStartRef.current = false
        void cancelProcess()
      }
    },
    [startSession, cancelProcess]
  )
  const requestCancel = useCallback(async () => {
    if (awaitingSessionRef.current) {
      cancelOnStartRef.current = true
      return
    }
    await cancelProcess()
  }, [cancelProcess])
  const { orders, allOrders, stats, startFullProcess, fetchOrders } =
    useOrders()
  const {
    filteredCompanies,
    selectedCompany,
    filters,
    hasActiveFilters,
    selectionMode,
    selectCompany,
    setFilter,
    clearFilters,
    searchCompanies,
    isLoading: companiesLoading,
    isRestored: companiesRestored,
    segments,
    plans
  } = useCompanies()
  const { roiData, isLoading: roiLoading, fetchRoi, clearRoi } = useRoi()
  const { startGroupProcess } = useSegmentProcess()

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [startDate, setStartDate] = useState(initialUrlFilters.startDate ?? '')
  const [endDate, setEndDate] = useState(initialUrlFilters.endDate ?? '')
  const [forceCache, setForceCache] = useState(false)
  const [liveAttributionWindow, setLiveAttributionWindow] = useState<LiveAttributionWindow>(
    initialUrlFilters.liveAttributionWindow ?? '24h'
  )
  const [liveStats, setLiveStats] = useState<LiveAttributionStats | null>(null)
  // Conversão por story da última execução stories (byStory). Como o resultado
  // não persiste nas orders, vive por execução — igual ao liveStats.
  const [storyConversions, setStoryConversions] = useState<StoryConversion[] | null>(null)
  // Diagnóstico de produtos/audiência da última execução. Mesma regra do
  // `byStory`: cache-hit vem sem, e manter o anterior é melhor que zerar —
  // o que NÃO pode é o vazio chegar na tela sem o estado que o explica.
  const [productDiag, setProductDiag] = useState<{
    byProduct: any[] | null
    topViewed: StoryConversion[] | null
    estado: any
    motivo: string | null
    snapshotsAvailable: boolean | null
  } | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [processedDates, setProcessedDates] = useState<{
    start: string
    end: string
  } | null>(null)
  const [segmentResult, setSegmentResult] =
    useState<SegmentProcessResult | null>(null)
  // Filtros da execução em andamento e os do resultado que cada seção mostra.
  // É o que deixa o painel dizer "isto é de outro período/janela" quando o
  // filtro muda depois do processamento (ver `process-params.ts`).
  const runningParamsRef = useRef<ProcessParams | null>(null)
  const [resultParams, setResultParams] = useState<{
    stories: ProcessParams | null
    live: ProcessParams | null
    group: ProcessParams | null
  }>({ stories: null, live: null, group: null })
  // Mudança de filtro pedida com um processamento rodando: espera a confirmação.
  const pendingChangesRef = useRef<Array<() => void>>([])
  const [pendingChange, setPendingChange] = useState<string[] | null>(null)
  const showToast = useCallback((msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }, [])

  const handleProductModeChange = useCallback((mode: ProductMode) => {
    setProductMode(mode)
    if (mode === 'live') clearFilters()
  }, [clearFilters])

  // When progress arrives from WebSocket, disable isStarting
  useEffect(() => {
    if (progress) setIsStarting(false)
  }, [progress])

  // Initialize dates (previous complete month) — a menos que a URL
  // compartilhada já tenha trazido o período.
  useEffect(() => {
    if (initialUrlFilters.startDate && initialUrlFilters.endDate) return

    const now = new Date()
    const prevMonth = now.getMonth() - 1
    const year = prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear()
    const month = prevMonth < 0 ? 11 : prevMonth
    const lastDay = new Date(year, month + 1, 0).getDate()
    const m = String(month + 1).padStart(2, '0')

    setStartDate(`${year}-${m}-01`)
    setEndDate(`${year}-${m}-${String(lastDay).padStart(2, '0')}`)
  }, [])

  // Espelha os filtros ativos na URL (link compartilhável). Só depois da
  // restauração inicial de empresas — senão o primeiro render (empresa ainda
  // null) apagaria o ?company= do link recém-aberto antes de ele ser aplicado.
  useEffect(() => {
    if (!companiesRestored) return
    writeUrlFilters({
      companyKey: selectedCompany?.key ?? null,
      segment: filters.segment,
      planType: filters.planType,
      startDate: startDate || null,
      endDate: endDate || null,
      liveAttributionWindow,
      productMode,
    })
  }, [companiesRestored, selectedCompany, filters, startDate, endDate, liveAttributionWindow, productMode])

  const buildParams = useCallback(
    (mode: ProductMode): ProcessParams => ({
      mode,
      selection: selectedCompany ? 'company' : 'group',
      companyKey: selectedCompany?.key ?? null,
      companyTitle: selectedCompany?.title ?? null,
      groupKeys: selectedCompany ? null : filteredCompanies.map(c => c.key).sort(),
      startDate,
      endDate,
      window: mode === 'live' ? liveAttributionWindow : null,
    }),
    [selectedCompany, filteredCompanies, startDate, endDate, liveAttributionWindow]
  )

  // When company is selected, load existing orders and reset state
  useEffect(() => {
    if (selectedCompany && startDate && endDate) {
      fetchOrders(selectedCompany.key, startDate, endDate)
      setProcessedDates(null)
      setSegmentResult(null)
      // Live stats são por execução (não persistem no /api/orders) — limpa ao
      // trocar de empresa. Aparecem de novo após o próximo "Processar Completo".
      setLiveStats(null)
      setStoryConversions(null)
      // Os pedidos recarregados acima são DESTA empresa e período.
      setResultParams(prev => ({ ...prev, stories: buildParams('stories'), live: null }))
    }
  }, [selectedCompany, fetchOrders]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear results when switching to group mode
  useEffect(() => {
    if (selectionMode === 'group') {
      setProcessedDates(null)
    }
  }, [selectionMode])

  // When processing completes, reload orders or capture segment result.
  // Os 2 modos (stories e live) atualizam CARDS DIFERENTES — um não invalida o outro.
  //
  // Cada `complete` é tratado UMA vez e com os filtros DA EXECUÇÃO. Antes o
  // efeito dependia das datas e da janela atuais: trocar o período depois do
  // resultado rodava tudo de novo e o card de live passava a exibir a janela
  // nova sobre os números calculados com a antiga.
  const handledCompleteRef = useRef<ProcessProgress | null>(null)
  useEffect(() => {
    if (progress?.step !== 'complete' || handledCompleteRef.current === progress) return
    handledCompleteRef.current = progress
    const run = runningParamsRef.current
    if (!run || !run.startDate || !run.endDate) return

    if (run.selection === 'company' && run.companyKey) {
      // Resultado de outra empresa (trocada sem passar pelo cancelamento) não entra.
      if (selectedCompany?.key !== run.companyKey) return
      const data = progress.data as Record<string, any> | undefined
      const completedMode: 'stories' | 'live' = data?.mode === 'live' ? 'live' : 'stories'

      if (completedMode === 'stories') {
        // Stories atualiza CompanyStatsCard (via reload das orders persistidas).
        fetchOrders(run.companyKey, run.startDate, run.endDate)
        setProcessedDates({ start: run.startDate, end: run.endDate })
        setResultParams(prev => ({ ...prev, stories: run }))
        // byStory vem no result da execução (cache-hit vem sem — mantém o anterior)
        if (Array.isArray(data?.byStory)) setStoryConversions(data.byStory as StoryConversion[])
        // O estado é gravado SEMPRE, mesmo em cache-hit: é ele que diz por que
        // as listas estão vazias, e sem isso a tela volta a ser ambígua.
        setProductDiag({
          byProduct: Array.isArray(data?.byProduct) ? data.byProduct : null,
          topViewed: Array.isArray(data?.topViewedStories) ? (data.topViewedStories as StoryConversion[]) : null,
          estado: data?.topViewedEstado ?? 'nao_calculado',
          motivo: data?.topViewedMotivo ?? null,
          snapshotsAvailable: typeof data?.snapshotsAvailable === 'boolean' ? data.snapshotsAvailable : null,
        })
      } else {
        // Live atualiza LiveStatsCard. NÃO chama fetchOrders (não persistimos
        // flags de live nas orders ainda).
        if (data && typeof data.liveMatched === 'number') {
          setResultParams(prev => ({ ...prev, live: run }))
          setLiveStats({
            window: run.window ?? liveAttributionWindow,
            liveMatched: data.liveMatched ?? 0,
            liveInfluencedRevenue: data.liveInfluencedRevenue ?? 0,
            liveDirectMatched: data.liveDirectMatched ?? 0,
            liveDirectRevenue: data.liveDirectRevenue ?? 0,
            liveMetrics: data.liveMetrics ?? undefined,
            liveCaveats: Array.isArray(data.liveCaveats) ? data.liveCaveats : undefined,
            byLive: Array.isArray(data.byLive) ? data.byLive : [],
          })
        }
      }
    } else if (run.selection === 'group' && progress.data) {
      // Group processing — sempre stories por enquanto.
      const result = progress.data as SegmentProcessResult
      setSegmentResult(result)
      setProcessedDates({ start: run.startDate, end: run.endDate })
      setLiveStats(null)
      setResultParams(prev => ({ ...prev, group: run }))
    }
  }, [progress, selectedCompany, fetchOrders, liveAttributionWindow])


  // Fetch ROI when stats are updated (only for company mode). O custo é do
  // período DOS PEDIDOS carregados (`resultParams.stories`), não do filtro: com
  // o filtro, trocar o mês dividia a receita antiga pelo custo do mês novo.
  const storiesStart = resultParams.stories?.startDate
  const storiesEnd = resultParams.stories?.endDate
  useEffect(() => {
    if (
      stats &&
      selectedCompany &&
      storiesStart &&
      storiesEnd &&
      selectionMode === 'company'
    ) {
      if (stats.influencedRevenue > 0) {
        fetchRoi(
          selectedCompany.key,
          storiesStart,
          storiesEnd,
          stats.influencedRevenue
        )
      } else {
        clearRoi()
      }
    }
  }, [
    stats,
    selectedCompany,
    storiesStart,
    storiesEnd,
    fetchRoi,
    clearRoi,
    selectionMode
  ])

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handleStartProcess = async (
    options: any
  ): Promise<{ sessionId: string }> => {
    clearProgress()
    setIsStarting(true)
    setSegmentResult(null)
    awaitingSessionRef.current = true
    cancelOnStartRef.current = false
    try {
      if (selectionMode === 'company' && selectedCompany) {
        // options já vem do ProcessButton com mode/liveAttributionWindow definidos
        runningParamsRef.current = buildParams(options?.mode === 'live' ? 'live' : 'stories')
        return await startFullProcess(options)
      }
      runningParamsRef.current = buildParams('stories')
      // Modo grupo continua processando 'stories' (live por grupo viria depois).
      return await startGroupProcess({
        companyKeys: filteredCompanies.map(c => c.key),
        startDate,
        endDate,
        forceDownload: forceCache,
        forceProcess: forceCache,
      })
    } catch (err) {
      setIsStarting(false)
      awaitingSessionRef.current = false
      cancelOnStartRef.current = false
      throw err
    }
  }

  // ── Mudança de filtro com processamento rodando ────────────────────────────
  // O processamento usa os filtros do clique; mudar um deles no meio deixava a
  // execução seguir na API e o resultado chegar com os filtros velhos sobre a
  // tela nova. Agora a mudança pede confirmação e, confirmada, cancela antes.
  const isRunning = isStarting || (progress !== null && progress.step !== 'complete' && progress.step !== 'error')
  const guardChange = (what: string, changed: boolean, apply: () => void) => {
    if (!changed || !isRunning) {
      apply()
      return
    }
    // Várias mudanças no mesmo clique ("limpar tudo") viram UMA confirmação.
    pendingChangesRef.current.push(apply)
    setPendingChange(prev => (prev ? (prev.includes(what) ? prev : [...prev, what]) : [what]))
  }
  const confirmPendingChange = async () => {
    const applies = pendingChangesRef.current
    pendingChangesRef.current = []
    setPendingChange(null)
    await requestCancel()
    for (const apply of applies) apply()
  }
  const discardPendingChange = () => {
    pendingChangesRef.current = []
    setPendingChange(null)
  }

  const guardedSelectCompany = (company: Company | null) =>
    guardChange('a empresa', (company?.key ?? null) !== (selectedCompany?.key ?? null), () => selectCompany(company))
  const guardedSetFilter = (key: 'segment' | 'planType', value: string | null) =>
    guardChange('os filtros do grupo', filters[key] !== value, () => setFilter(key, value))
  const guardedClearFilters = () => guardChange('os filtros do grupo', hasActiveFilters, clearFilters)
  const guardedProductModeChange = (mode: ProductMode) =>
    guardChange('o produto', mode !== productMode, () => handleProductModeChange(mode))
  const guardedDateChange = (newStart: string, newEnd: string) =>
    guardChange('o período', newStart !== startDate || newEnd !== endDate, () => handleDateChange(newStart, newEnd))
  const guardedWindowChange = (w: LiveAttributionWindow) =>
    guardChange('a janela de atribuição', w !== liveAttributionWindow, () => setLiveAttributionWindow(w))

  // ── Resultado desatualizado ────────────────────────────────────────────────
  // A seção visível mostra o resultado da última execução; se os filtros
  // atuais não são os dela, a tela diz isso em vez de parecer atualizada.
  const shownParams = productMode === 'live'
    ? resultParams.live
    : selectionMode === 'group' ? resultParams.group : resultParams.stories
  const staleChanges = selectionMode
    ? describeParamChanges(shownParams, buildParams(productMode === 'live' ? 'live' : 'stories'))
    : []
  const staleResult = shownParams && staleChanges.length > 0
    ? { changed: joinChanges(staleChanges), shown: describeParams(shownParams) }
    : null

  const datesChanged =
    processedDates !== null &&
    (processedDates.start !== startDate || processedDates.end !== endDate)

  const isFormValid =
    startDate !== '' && endDate !== '' &&
    (productMode === 'live'
      ? selectedCompany !== null
      : selectedCompany !== null || hasActiveFilters)

  return (
    <div className="app-shell">
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-logo">W</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Widde Lab</div>
            <div style={{ fontSize: 10, color: 'var(--ink-500)' }}>Analytics</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {/* VISÃO GERAL */}
          <div className="sidebar-section-label">Visão Geral</div>
          <button className={`sidebar-item ${activePage === 'dashboard' ? 'active' : ''}`} onClick={() => setActivePage('dashboard')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            Dashboard
          </button>
          <button className="sidebar-item sidebar-item--dim" disabled>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Empresas
            <span className="sidebar-soon">em breve</span>
          </button>
          <button className="sidebar-item sidebar-item--dim" disabled>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            Segmentos
            <span className="sidebar-soon">em breve</span>
          </button>

          {/* ANÁLISE */}
          <div className="sidebar-section-label">Análise</div>
          <button className={`sidebar-item ${activePage === 'data-quality' ? 'active' : ''}`} onClick={() => setActivePage('data-quality')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Qualidade de Dados
            <span className="sidebar-badge">7</span>
          </button>
          <button className={`sidebar-item ${activePage === 'explorer' ? 'active' : ''}`} onClick={() => setActivePage('explorer')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            Explorador
          </button>

          {/* LAB */}
          <div className="sidebar-section-label">Lab</div>
          <button className={`sidebar-item ${activePage === 'metrics' ? 'active' : ''}`} onClick={() => setActivePage('metrics')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            Catálogo de Métricas
          </button>
          {userMode === 'scientist' && (
            <button className="sidebar-item sidebar-item--dim" disabled>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/></svg>
              Construtor
              <span className="pill pill-pink" style={{ fontSize: 9, marginLeft: 'auto' }}>NEW</span>
            </button>
          )}

          {/* SISTEMA */}
          <div className="sidebar-section-label">Sistema</div>
          <button className={`sidebar-item ${activePage === 'settings' ? 'active' : ''}`} onClick={() => setActivePage('settings')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Configurações
          </button>
        </nav>
      </aside>

      {/* MAIN */}
      <div className="main-panel">
        {/* TOPBAR */}
        <header className="topbar">
          {/* Row 1 — main controls */}
          <div className="topbar-row">
            {/* Left: filter bar — shrinks but stays legible */}
            <FilterBar
              productMode={productMode}
              onProductModeChange={guardedProductModeChange}
              segments={segments}
              plans={plans}
              filters={filters}
              onFilterChange={guardedSetFilter}
              onClearFilters={guardedClearFilters}
              selectedCompany={selectedCompany}
              onSelectCompany={guardedSelectCompany}
              searchCompanies={searchCompanies}
              isLoading={companiesLoading}
            />

            {/* Right group — fixed, never wraps or shrinks */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
              <DatePicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={guardedDateChange}
                compact
              />
              {productMode === 'live' && (
                <AttributionPill
                  value={liveAttributionWindow}
                  onChange={guardedWindowChange}
                />
              )}
              <div style={{ width: 1, height: 24, background: 'var(--ink-200)' }} />
              <span style={{ fontSize: 12, color: 'var(--ink-500)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</span>
              <button className="btn btn-sm btn-ghost" onClick={onSignOut}>Sair</button>
            </div>
          </div>
        </header>

        {/* PAGE CONTENT */}
        <main className="main-content">
          {activePage === 'dashboard' && (
            <DashboardPage
              productMode={productMode}
              selectionMode={selectionMode}
              selectedCompany={selectedCompany}
              startDate={startDate}
              endDate={endDate}
              forceCache={forceCache}
              setForceCache={setForceCache}
              liveAttributionWindow={liveAttributionWindow}
              liveStats={liveStats}
              storyConversions={storyConversions}
              productDiag={productDiag}
              stats={stats}
              allOrders={allOrders}
              orders={orders}
              roiData={roiData}
              roiLoading={roiLoading}
              datesChanged={datesChanged}
              segmentResult={segmentResult}
              progress={progress}
              isConnected={isConnected}
              isStarting={isStarting}
              isFormValid={isFormValid}
              showToast={showToast}
              onStartProcess={handleStartProcess}
              onSessionStart={handleSessionStart}
              onCancelProcess={isRunning ? requestCancel : undefined}
              staleResult={staleResult}
              resultPeriod={shownParams ? { startDate: shownParams.startDate, endDate: shownParams.endDate } : null}
              setSelectedOrder={setSelectedOrder}
            />
          )}
          {activePage === 'data-quality' && (
            <DataQualityPage
              segmentResult={segmentResult}
              productMode={productMode}
              startDate={startDate}
              endDate={endDate}
              selectedCompany={selectedCompany}
              onStartProcess={handleStartProcess}
              onSessionStart={handleSessionStart}
              progress={progress}
              isConnected={isConnected}
              isFormValid={isFormValid}
              forceCache={forceCache}
              setForceCache={setForceCache}
              isStarting={isStarting}
            />
          )}
          {activePage === 'explorer' && (
            <ExplorerPage segmentResult={segmentResult} productMode={productMode} />
          )}
          {activePage === 'metrics' && (
            <MetricsCatalogPage userMode={userMode} productMode={productMode} />
          )}
          {activePage === 'settings' && <SettingsPage />}
        </main>
      </div>

      <ProcessDebugPanel
        sessionId={processSessionId}
        status={processStatus}
        startedAt={processStartedAt}
        events={processEvents}
        isConnected={isConnected}
        onCancel={requestCancel}
      />

      {pendingChange && (
        <ConfirmProcessChangeModal
          changing={pendingChange}
          running={runningParamsRef.current ? describeParams(runningParamsRef.current) : null}
          onKeep={discardPendingChange}
          onCancelAndApply={confirmPendingChange}
        />
      )}

      {/* Modal de detalhes — fora do main-panel mas dentro do app-shell */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'var(--ink-900)',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 14,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.2s ease',
            zIndex: 9999
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

function AttributionPill({
  value,
  onChange,
}: {
  value: LiveAttributionWindow
  onChange: (v: LiveAttributionWindow) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Janela de atribuição Live Commerce"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 999,
          border: '1px solid #fecdab',
          background: open ? '#ffe8d6' : '#fff4ec',
          cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 600, color: '#c2410c',
        }}
      >
        {/* Live dot */}
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.25)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#c2410c' }}>LC</span>
        <span>Atribuição {LIVE_ATTRIBUTION_WINDOW_LABELS[value]}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
          background: 'var(--paper)', border: '1px solid var(--ink-200)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)', minWidth: 160,
          overflow: 'hidden', padding: 4,
        }}>
          <div style={{ fontSize: 10, color: 'var(--ink-500)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', padding: '6px 10px 4px' }}>
            Janela de atribuição
          </div>
          {LIVE_ATTRIBUTION_WINDOW_ORDER.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '7px 10px', border: 'none', borderRadius: 6,
                background: value === opt ? '#fff4ec' : 'transparent',
                color: value === opt ? '#c2410c' : 'var(--ink-700)',
                cursor: 'pointer', fontSize: 13, fontWeight: value === opt ? 600 : 400,
                fontFamily: 'inherit',
              }}
            >
              {LIVE_ATTRIBUTION_WINDOW_LABELS[opt]}
              {value === opt && (
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default App

